/* iter-audit1 — Prompt Audit Drawer.
 *
 * "See exactly what Claude receives" — dry-run preview of the system +
 * user prompt Claude gets when the author drafts a chapter, plus a
 * coverage roll-up (populated / missing fields, sorted by impact).
 *
 * No tokens are spent — the backend runs `assemble_prompt_preview`
 * against the framework doc and returns the fully-resolved strings.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Loader2, Sparkles, CheckCircle2, AlertCircle, Copy, ChevronDown } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const P = {
  paper: '#F7F4EE', ink: '#24201B', inkSoft: '#5B5348',
  oxblood: '#6A2E2E', gold: '#C8A96E', verdant: '#2D4A3E',
};

export default function PromptAuditDrawer({ framework, defaultChapter = 1, onClose }) {
  const chapters = framework?.chapters || [];
  const [chapterNumber, setChapterNumber] = useState(defaultChapter);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('system');   // system | user | coverage

  const run = useCallback(async () => {
    if (!framework?.id) return;
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/framework/${framework.id}/prompt-preview`,
        { chapter_number: chapterNumber, user_guidance: '' },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not assemble preview.');
    } finally { setLoading(false); }
  }, [chapterNumber, framework?.id]);

  useEffect(() => { run(); }, [run]);

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard.');
    } catch { toast.error('Clipboard blocked by browser.'); }
  };

  const cov = preview?.coverage;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="prompt-audit-drawer"
    >
      <div
        className="flex h-full w-full max-w-3xl flex-col border-l shadow-2xl"
        style={{ backgroundColor: P.paper, borderColor: `${P.gold}88` }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between border-b px-4 py-3"
          style={{ borderColor: `${P.gold}55` }}
        >
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" style={{ color: P.oxblood }} />
              <h2
                className="text-[16px] font-semibold"
                style={{ fontFamily: 'Cormorant Garamond, serif', color: P.ink }}
              >
                See exactly what Claude receives
              </h2>
            </div>
            <p className="mt-0.5 text-[11px]" style={{ color: P.inkSoft }}>
              Dry-run of the chapter draft prompt. No tokens spent.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="prompt-audit-close"
            className="rounded-full p-1"
            style={{ color: P.inkSoft }}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Chapter picker */}
        <div className="flex items-center gap-2 border-b px-4 py-2" style={{ borderColor: `${P.gold}33` }}>
          <label className="text-[11px] font-semibold uppercase tracking-wider"
                 style={{ color: P.oxblood }}>Chapter</label>
          <select
            value={chapterNumber}
            onChange={(e) => setChapterNumber(Number(e.target.value))}
            data-testid="prompt-audit-chapter"
            className="rounded-sm border px-2 py-1 text-[12.5px]"
            style={{ borderColor: `${P.gold}66`, color: P.ink, backgroundColor: P.paper }}
          >
            {chapters.map((c) => (
              <option key={c.number} value={c.number}>
                Ch {c.number}{c.title ? ` · ${c.title}` : ''}
              </option>
            ))}
          </select>
          {preview && (
            <div className="ml-auto flex items-center gap-3 text-[11px]" style={{ color: P.inkSoft }}>
              <span data-testid="prompt-audit-tokens">
                ~<b style={{ color: P.ink }}>{preview.tokens_estimate.toLocaleString()}</b> input tokens
              </span>
              <span data-testid="prompt-audit-overall">
                Coverage <b style={{ color: cov?.overall_pct >= 70 ? P.verdant : P.oxblood }}>
                  {Math.round(cov?.overall_pct || 0)}%
                </b>
              </span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b px-4 py-1.5" style={{ borderColor: `${P.gold}33` }}>
          {['coverage', 'system', 'user'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`prompt-audit-tab-${t}`}
              className="rounded-sm border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider"
              style={{
                borderColor: tab === t ? P.oxblood : 'transparent',
                backgroundColor: tab === t ? `${P.oxblood}12` : 'transparent',
                color: tab === t ? P.oxblood : P.inkSoft,
              }}
            >
              {t === 'coverage' ? 'Coverage' : t === 'system' ? 'System Prompt' : 'User Prompt'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin" style={{ color: P.oxblood }} />
            </div>
          ) : !preview ? (
            <div className="p-6 text-center text-[12px]" style={{ color: P.inkSoft }}>
              Pick a chapter to preview its prompt.
            </div>
          ) : tab === 'coverage' ? (
            <CoverageView preview={preview} />
          ) : (
            <PromptText
              text={tab === 'system' ? preview.system_prompt : preview.user_prompt}
              onCopy={() => copy(tab === 'system' ? preview.system_prompt : preview.user_prompt)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CoverageView({ preview }) {
  const { populated_fields, missing_fields, coverage } = preview;
  return (
    <div className="space-y-4 p-4">
      {/* Populated */}
      <section data-testid="prompt-audit-populated">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider"
             style={{ color: P.verdant }}>
          <CheckCircle2 className="h-3.5 w-3.5" />
          Populated ({populated_fields.length})
        </div>
        {populated_fields.length === 0 ? (
          <div className="text-[11.5px] italic" style={{ color: P.inkSoft }}>
            Nothing filled in yet. Even a rough placeholder helps Claude land the beat.
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-1.5">
            {populated_fields.map((f) => (
              <li
                key={`${f.group}-${f.key}`}
                className="flex items-center gap-1.5 rounded-sm border p-1.5 text-[11px]"
                style={{ borderColor: `${P.verdant}44`, backgroundColor: `${P.verdant}0d`, color: P.ink }}
              >
                <CheckCircle2 className="h-3 w-3" style={{ color: P.verdant }} />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-semibold">{f.label}</div>
                  <div className="truncate text-[10px]" style={{ color: P.inkSoft }}>
                    {f.group}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Missing / nudges */}
      <section data-testid="prompt-audit-missing">
        <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider"
             style={{ color: P.oxblood }}>
          <AlertCircle className="h-3.5 w-3.5" />
          High-impact blanks ({missing_fields.length})
        </div>
        {missing_fields.length === 0 ? (
          <div className="text-[11.5px] italic" style={{ color: P.verdant }}>
            All top-weight fields are filled — Claude has everything it needs.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {missing_fields.slice(0, 12).map((f) => (
              <li
                key={`${f.group}-${f.key}`}
                className="rounded-sm border p-2 text-[11.5px]"
                style={{ borderColor: `${P.oxblood}44`, backgroundColor: `${P.oxblood}09`, color: P.ink }}
              >
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold">{f.label}</span>
                  <span className="rounded-full px-1.5 py-0.5 text-[9.5px] font-semibold uppercase"
                        style={{ backgroundColor: `${P.oxblood}18`, color: P.oxblood }}>
                    +{Math.round(f.weight * 100)}% lift
                  </span>
                </div>
                <div className="mt-0.5 text-[10.5px] italic" style={{ color: P.inkSoft }}>
                  {f.tip}
                </div>
                <div className="mt-0.5 text-[10px]" style={{ color: P.inkSoft }}>
                  Scope: {f.group}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Group breakdown */}
      <section className="rounded-sm border p-2 text-[11px]"
               style={{ borderColor: `${P.gold}55`, backgroundColor: `${P.gold}0e`, color: P.ink }}>
        <div className="mb-1 font-semibold uppercase tracking-wider" style={{ color: P.oxblood }}>
          Coverage by area
        </div>
        <div className="grid grid-cols-3 gap-2">
          <MiniBar label="Framework"  pct={coverage?.framework?.pct}  />
          <MiniBar label="Chapter"    pct={coverage?.chapter?.pct}    />
          <MiniBar label="Characters" pct={
            coverage?.characters?.per_character?.length
              ? (coverage.characters.per_character.reduce((a,c) => a + c.pct, 0)
                  / coverage.characters.per_character.length)
              : 0
          } />
        </div>
      </section>
    </div>
  );
}

function MiniBar({ label, pct }) {
  const p = Math.round(pct || 0);
  const color = p >= 70 ? P.verdant : p >= 40 ? P.gold : P.oxblood;
  return (
    <div>
      <div className="mb-0.5 flex items-baseline justify-between text-[10px]">
        <span>{label}</span>
        <span className="font-mono-stat font-semibold" style={{ color }}>{p}%</span>
      </div>
      <div className="h-1 w-full overflow-hidden rounded-full" style={{ backgroundColor: `${color}22` }}>
        <div className="h-full" style={{ width: `${p}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function PromptText({ text, onCopy }) {
  return (
    <div className="relative p-4">
      <button
        onClick={onCopy}
        data-testid="prompt-audit-copy"
        className="absolute right-6 top-6 inline-flex items-center gap-1 rounded-sm border px-2 py-1 text-[10.5px] font-semibold hover:bg-primary/10"
        style={{ borderColor: `${P.oxblood}66`, color: P.oxblood }}
      >
        <Copy className="h-3 w-3" /> Copy
      </button>
      <pre
        className="whitespace-pre-wrap rounded-sm border p-3 text-[11.5px] leading-relaxed"
        style={{
          borderColor: `${P.gold}55`,
          backgroundColor: '#fbf6e6',
          color: P.ink,
          fontFamily: '"SF Mono", Menlo, monospace',
          maxHeight: 'none',
        }}
        data-testid="prompt-audit-text"
      >
        {text || '(empty)'}
      </pre>
    </div>
  );
}
